import { useEffect, useId, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';

export interface DestinoDeNavegacion {
  path: string;
  label: string;
  end?: boolean;
}

interface MenuMovilProps {
  items: DestinoDeNavegacion[];
  /** Conversaciones esperando atención. 0 no se muestra. */
  pendientes: number;
}

/**
 * La navegación cuando la barra no entra (GKTIBO-108).
 *
 * ## Por qué existe
 *
 * El 2026-09-22 el PO mandó pantallazos del panel en un teléfono: la barra se
 * lee `Tibo CRM · Tenants · Conversaciones 1 · Conta…` y se corta.
 * **«Tickets» y «Métricas» no estaban apretados: eran inalcanzables.** Dos de
 * las cinco secciones del producto no se podían abrir desde un celular, y nada
 * indicaba que existieran.
 *
 * ## Por qué un menú y no achicar
 *
 * Con cinco destinos y ancho de teléfono, reducir el texto deja cinco etiquetas
 * de tres letras que nadie lee. Y esconderlos es lo correcto acá por una razón
 * de producto además de espacio: el dueño de un negocio no debería ver
 * «Tenants», que es una pantalla de operación nuestra (GKTIBO-101).
 *
 * ## El número va AFUERA
 *
 * El aviso de conversaciones esperando se muestra en el botón cerrado, no sólo
 * adentro. Si viviera sólo en el menú desplegado habría que abrirlo para
 * enterarse — y entonces no avisa nada, que es justo lo contrario de para qué
 * existe la bandeja.
 */
export function MenuMovil({ items, pendientes }: MenuMovilProps) {
  const [abierto, setAbierto] = useState(false);
  const idDelPanel = useId();
  const contenedor = useRef<HTMLDivElement>(null);

  // Escape cierra. Abrir algo que tapa la pantalla sin forma de cerrarlo con el
  // teclado es una trampa para quien no usa el mouse.
  useEffect(() => {
    if (!abierto) return;
    const alTeclear = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAbierto(false);
    };
    document.addEventListener('keydown', alTeclear);
    return () => document.removeEventListener('keydown', alTeclear);
  }, [abierto]);

  // Tocar fuera también cierra: en un teléfono es el gesto que todos esperan.
  useEffect(() => {
    if (!abierto) return;
    const alTocar = (e: MouseEvent) => {
      if (!contenedor.current?.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('mousedown', alTocar);
    return () => document.removeEventListener('mousedown', alTocar);
  }, [abierto]);

  return (
    <div ref={contenedor} className="relative md:hidden">
      <button
        type="button"
        aria-label="Menú"
        aria-expanded={abierto}
        aria-controls={idDelPanel}
        onClick={() => setAbierto((v) => !v)}
        className="flex items-center gap-2 rounded-md px-3 py-1.5 text-content-secondary hover:bg-surface-sunken"
      >
        <span aria-hidden="true" className="text-lg leading-none">
          ☰
        </span>
        {pendientes > 0 ? (
          <span
            data-testid="pendientes-en-menu"
            aria-label={`${pendientes} conversaciones esperando atención`}
            className="rounded-full bg-danger px-1.5 py-0.5 text-xs font-semibold text-white"
          >
            {pendientes}
          </span>
        ) : null}
      </button>

      {abierto ? (
        <nav
          id={idDelPanel}
          aria-label="Navegación principal"
          className="absolute left-0 top-full z-50 mt-1 min-w-48 rounded-md border border-edge-subtle bg-surface-raised py-1 shadow-lg"
        >
          {items.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              // Elegir un destino cierra: si quedara abierto taparía la
              // pantalla a la que acabás de navegar.
              onClick={() => setAbierto(false)}
              className={({ isActive }) =>
                `flex items-center justify-between px-4 py-2.5 text-sm ${
                  isActive
                    ? 'bg-surface-brand-soft font-medium text-content-brand'
                    : 'text-content-secondary'
                }`
              }
            >
              <span>{item.label}</span>
              {item.path === '/conversations' && pendientes > 0 ? (
                <span className="ml-3 rounded-full bg-danger px-1.5 py-0.5 text-xs font-semibold text-white">
                  {pendientes}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
      ) : null}
    </div>
  );
}
